import {
  mapWidgetResponseToStatus,
  SystemStatusLevel,
  SystemStatusResult,
} from '@choco-admin/utils';
import { useQuery } from '@tanstack/react-query';

import { STATUS_PAGE_URL } from '../viteEnv';

/**
 * incident.io Widget API integration
 *
 * Endpoint: GET <VITE_STATUS_PAGE_URL>/api/widget
 *   where VITE_STATUS_PAGE_URL is the published status page base URL,
 *   e.g. https://statuspage.incident.io/bechoco
 *
 * To find your URL: incident.io dashboard → Status pages → your page
 *   → Settings → Widget API. The URL and a live JSON preview are shown there.
 *
 * The endpoint is unauthenticated and publicly accessible (public pages only).
 */

const POLL_INTERVAL_MS = 120_000; // 2 minutes

export interface UseSystemStatusReturn {
  level: SystemStatusLevel;
  ongoingCount: number;
  maintenanceCount: number;
  /** True if VITE_STATUS_PAGE_URL is not configured */
  isStaticFallback: boolean;
  /** URL to open when the indicator is clicked (undefined when not configured) */
  statusPageUrl: string | undefined;
}

const STATIC_FALLBACK: UseSystemStatusReturn = {
  // 'unknown' renders as "No connection to incident.io" — a missing
  // VITE_STATUS_PAGE_URL must not masquerade as a healthy status.
  level: 'unknown',
  ongoingCount: 0,
  maintenanceCount: 0,
  isStaticFallback: true,
  statusPageUrl: undefined,
};

const UNKNOWN_RESULT: SystemStatusResult = {
  level: 'unknown',
  ongoingCount: 0,
  maintenanceCount: 0,
};

function getStatusPageBaseUrl(): string | undefined {
  const raw = STATUS_PAGE_URL;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  return raw.trim().replace(/\/$/, ''); // strip trailing slash
}

async function fetchWidgetStatus(baseUrl: string): Promise<SystemStatusResult> {
  const response = await fetch(`${baseUrl}/api/v1/summary`, {
    // No auth headers — this is a public endpoint.
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return UNKNOWN_RESULT;

  const json: unknown = await response.json();

  return mapWidgetResponseToStatus(json);
}

export function useSystemStatus(): UseSystemStatusReturn {
  const baseUrl = getStatusPageBaseUrl();

  const { data, isError } = useQuery({
    queryKey: ['systemStatus', baseUrl],
    queryFn: () => fetchWidgetStatus(baseUrl as string),
    enabled: !!baseUrl,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  if (!baseUrl) {
    return STATIC_FALLBACK;
  }

  return {
    // Network error or parse failure — fall to unknown so the UI shows the
    // static fallback tooltip rather than a false "operational".
    ...(isError ? UNKNOWN_RESULT : (data ?? UNKNOWN_RESULT)),
    isStaticFallback: false,
    statusPageUrl: baseUrl,
  };
}
